import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast, Toaster } from 'react-hot-toast';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';
const WHATSAPP_BACK_URL = 'https://wa.me/919315918562';

const emptyFullForm = {
  candidate_name: '',
  age: '',
  gender: '',
  qualification: '',
  trade: '',
  home_location: '',
  mobile: '',
};

function DetailRow({ icon, label, value }) {
  if (value == null || String(value).trim() === '' || String(value).trim() === 'NA') return null;
  return (
    <div style={styles.detailRow}>
      <span style={styles.detailIcon}>{icon}</span>
      <div style={{ minWidth: 0 }}>
        <div style={styles.detailLabel}>{label}</div>
        <div style={styles.detailValue}>{value}</div>
      </div>
    </div>
  );
}

function JobInterestJobPage() {
  const { supervisorId, jobId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const fromListFlag = (() => {
    try {
      return sessionStorage.getItem(`jobInterestFromList:${supervisorId}`) === '1';
    } catch {
      return false;
    }
  })();
  const fromJobsList = location.state?.from === 'list' || fromListFlag;
  const prefill = location.state?.prefill || null;
  const isDirectJobLink = !fromJobsList;

  const [loading, setLoading] = useState(true);
  const [supervisor, setSupervisor] = useState(null);
  const [job, setJob] = useState(null);
  const [showApply, setShowApply] = useState(false);
  const [candidateName, setCandidateName] = useState(prefill?.candidate_name || '');
  const [mobile, setMobile] = useState(prefill?.mobile || '');
  const [fullForm, setFullForm] = useState({
    ...emptyFullForm,
    ...(prefill || {}),
  });
  const [submitting, setSubmitting] = useState(false);
  const [contact, setContact] = useState(null);
  const applyFormRef = useRef(null);
  const firstInputRef = useRef(null);

  useEffect(() => {
    if (!showApply || contact) return;
    const t = window.setTimeout(() => {
      applyFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      window.setTimeout(() => {
        firstInputRef.current?.focus({ preventScroll: true });
      }, 400);
    }, 80);
    return () => window.clearTimeout(t);
  }, [showApply, contact]);

  const openApply = () => setShowApply(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API_URL}/api/job-interest/${supervisorId}/jobs/${jobId}`);
        const data = await res.json();
        if (!data.success) {
          toast.error(data.message || 'Job not found');
          setJob(null);
          setSupervisor(null);
          return;
        }
        setSupervisor(data.supervisor);
        setJob(data.job);
        setFullForm((prev) => ({
          ...prev,
          ...(prefill || {}),
          qualification: prev.qualification || prefill?.qualification || data.job?.qualification || '',
          trade:
            prev.trade ||
            prefill?.trade ||
            (data.job?.trade && data.job.trade !== 'All' ? data.job.trade : '') ||
            '',
        }));
        if (prefill?.candidate_name) setCandidateName(prefill.candidate_name);
        if (prefill?.mobile) setMobile(prefill.mobile);
      } catch (err) {
        console.error(err);
        toast.error('Failed to load job');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [supervisorId, jobId]);

  const listPath = `/job-interest/${supervisorId}`;

  const goBackToJobsList = () => {
    navigate(listPath, { state: { resumeJobs: true } });
  };

  const handleBack = () => {
    if (fromJobsList) {
      goBackToJobsList();
      return;
    }
    window.location.href = WHATSAPP_BACK_URL;
  };

  const onFullFormChange = (e) => {
    const { name, value } = e.target;
    setFullForm((prev) => ({ ...prev, [name]: value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!job?.location) return toast.error('Job location missing');

    if (isDirectJobLink) {
      if (!candidateName.trim()) return toast.error('Name is required');
      if (mobile.replace(/\D/g, '').length < 10) return toast.error('Enter a valid 10-digit mobile');
    } else {
      if (!fullForm.candidate_name.trim()) return toast.error('Name is required');
      if (String(fullForm.mobile || '').replace(/\D/g, '').length < 10) {
        return toast.error('Enter a valid 10-digit mobile');
      }
      if (!fullForm.work_location && !job.location) return toast.error('Work location is required');
    }

    setSubmitting(true);
    try {
      const payload = isDirectJobLink
        ? {
            candidate_name: candidateName.trim(),
            mobile: mobile.replace(/\D/g, '').slice(-10),
            work_location: job.location,
            selected_job_ids: [Number(jobId)],
          }
        : {
            ...fullForm,
            mobile: String(fullForm.mobile || '').replace(/\D/g, '').slice(-10),
            work_location: job.location,
            selected_job_ids: [Number(jobId)],
          };

      const res = await fetch(`${API_URL}/api/job-interest/${supervisorId}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!data.success) {
        toast.error(data.message || 'Submit failed');
        return;
      }
      setContact((data.contacts || [])[0] || null);
      setShowApply(false);
      toast.success(data.merged ? 'Application updated' : 'Applied successfully');
    } catch (err) {
      console.error(err);
      toast.error('Submit failed');
    } finally {
      setSubmitting(false);
    }
  };

  const displayName = isDirectJobLink ? candidateName : fullForm.candidate_name;

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.shell}>
          <div style={styles.loadingCard}>
            <div style={styles.spinner} />
            <p style={{ margin: '12px 0 0', color: '#64748b' }}>Loading job…</p>
          </div>
        </div>
      </div>
    );
  }

  if (!job || !supervisor) {
    return (
      <div style={styles.page}>
        <Toaster position="top-center" />
        <div style={styles.shell}>
          <div style={styles.emptyCard}>
            <h1 style={{ ...styles.heroTitle, fontSize: 22 }}>Job unavailable</h1>
            <p style={styles.sub}>This opening may have been removed.</p>
            {fromJobsList ? (
              <Link to={listPath} state={{ resumeJobs: true }} style={styles.primaryLinkBtn}>
                Back to all jobs
              </Link>
            ) : (
              <a href={WHATSAPP_BACK_URL} style={styles.primaryLinkBtn}>Back to WhatsApp</a>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <Toaster position="top-center" />
      <div style={styles.shell}>
        <header style={styles.topBar}>
          <button type="button" onClick={handleBack} style={styles.backBtn}>
            {fromJobsList ? '← All jobs' : '← WhatsApp'}
          </button>
        </header>

        <div style={styles.heroCard}>
          <div style={styles.heroGlow} />
          <div style={styles.badge}>Open role</div>
          <h1 style={styles.heroTitle}>{job.job_post}</h1>
          <p style={styles.company}>{job.company_name}</p>
          <p style={styles.locationLine}>
            <span style={styles.pin}>📍</span>
            {job.location}
          </p>
          {(job.salary || job.hours) ? (
            <div style={styles.highlightRow}>
              {job.salary ? (
                <div style={styles.highlight}>
                  <div style={styles.highlightLabel}>Salary</div>
                  <div style={styles.highlightValue}>{job.salary}</div>
                </div>
              ) : null}
              {job.hours ? (
                <div style={styles.highlight}>
                  <div style={styles.highlightLabel}>Working hours</div>
                  <div style={styles.highlightValue}>{job.hours}</div>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>Requirements & benefits</h2>
          <div style={styles.detailsGrid}>
            <DetailRow icon="🎓" label="Qualification" value={job.qualification} />
            <DetailRow icon="🔧" label="Trade" value={job.trade} />
            <DetailRow icon="👤" label="Gender" value={job.gender_preference} />
            <DetailRow icon="🎂" label="Age" value={job.age_range} />
            <DetailRow icon="🍽" label="Canteen" value={job.canteen} />
            <DetailRow icon="🚌" label="Transport" value={job.transport_facility} />
          </div>
        </section>

        {job.description ? (
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>About this role</h2>
            <p style={styles.description}>{job.description}</p>
          </section>
        ) : null}

        <p style={styles.footerNote}>Shared by {supervisor.name} · Eduvantage / GoGig</p>

        {contact ? (
          <section style={styles.successBox}>
            <div style={styles.successIcon}>✓</div>
            <h2 style={{ ...styles.sectionTitle, marginBottom: 6 }}>You&apos;re applied!</h2>
            <p style={{ margin: '0 0 12px', color: '#134e4a', fontSize: 14 }}>
              Thank you, {displayName}. Reach out for this job:
            </p>
            <div style={styles.contactCard}>
              <div style={{ fontWeight: 800, fontSize: 16 }}>{contact.contact_person_name || 'Contact person'}</div>
              {contact.contact_person_number ? (
                <a href={`tel:${contact.contact_person_number}`} style={styles.phone}>
                  {contact.contact_person_number}
                </a>
              ) : (
                <p style={{ margin: '8px 0 0', color: '#64748b', fontSize: 14 }}>Contact number will be shared shortly.</p>
              )}
            </div>
            {!fromJobsList ? (
              <a href={WHATSAPP_BACK_URL} style={{ ...styles.primaryLinkBtn, marginTop: 16, display: 'inline-block' }}>
                Back to WhatsApp
              </a>
            ) : (
              <button type="button" onClick={goBackToJobsList} style={{ ...styles.outlineBtn, marginTop: 16 }}>
                Back to all jobs
              </button>
            )}
          </section>
        ) : null}

        {showApply && !contact ? (
          <form ref={applyFormRef} onSubmit={onSubmit} style={styles.applySheet}>
            <h2 style={styles.sectionTitle}>Apply for this job</h2>
            {isDirectJobLink ? (
              <p style={styles.applyHint}>Quick apply — name and mobile only.</p>
            ) : (
              <p style={styles.applyHint}>Fill your details to apply for this opening.</p>
            )}

            {isDirectJobLink ? (
              <>
                <label style={styles.label}>Full name *</label>
                <input
                  ref={firstInputRef}
                  value={candidateName}
                  onChange={(e) => setCandidateName(e.target.value)}
                  style={styles.input}
                  placeholder="Your name"
                  autoComplete="name"
                  required
                />
                <label style={styles.label}>Mobile number *</label>
                <input
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value)}
                  style={styles.input}
                  placeholder="10-digit mobile"
                  inputMode="tel"
                  autoComplete="tel"
                  required
                />
              </>
            ) : (
              <>
                <label style={styles.label}>Name *</label>
                <input
                  ref={firstInputRef}
                  name="candidate_name"
                  value={fullForm.candidate_name}
                  onChange={onFullFormChange}
                  style={styles.input}
                  required
                />

                <label style={styles.label}>Mobile number *</label>
                <input
                  name="mobile"
                  value={fullForm.mobile}
                  onChange={onFullFormChange}
                  style={styles.input}
                  inputMode="tel"
                  placeholder="10-digit mobile"
                  required
                />

                <div style={styles.row}>
                  <div style={{ flex: 1 }}>
                    <label style={styles.label}>Age</label>
                    <input name="age" value={fullForm.age} onChange={onFullFormChange} style={styles.input} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={styles.label}>Gender</label>
                    <select name="gender" value={fullForm.gender} onChange={onFullFormChange} style={styles.input}>
                      <option value="">Select</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>

                <label style={styles.label}>Qualification</label>
                <input name="qualification" value={fullForm.qualification} onChange={onFullFormChange} style={styles.input} />

                <label style={styles.label}>Trade</label>
                <input name="trade" value={fullForm.trade} onChange={onFullFormChange} style={styles.input} />

                <label style={styles.label}>Home location</label>
                <input name="home_location" value={fullForm.home_location} onChange={onFullFormChange} style={styles.input} />

                <p style={styles.workLocNote}>Work location: <strong>{job.location}</strong></p>
              </>
            )}

            <button type="submit" disabled={submitting} style={styles.primaryBtn}>
              {submitting ? 'Submitting…' : 'Submit application'}
            </button>
            <button type="button" onClick={() => setShowApply(false)} style={styles.textBtn}>
              Cancel
            </button>
          </form>
        ) : null}

        {!contact && !showApply ? <div style={{ height: 96 }} /> : null}
      </div>

      {!contact && !showApply ? (
        <div style={styles.stickyBar}>
          <button type="button" onClick={handleBack} style={styles.stickySecondary}>
            Back
          </button>
          <button type="button" onClick={openApply} style={styles.stickyPrimary}>
            Apply now
          </button>
        </div>
      ) : null}

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes applySlideIn {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(180deg, #0f766e 0%, #0f766e 140px, #f1f5f9 140px, #f8fafc 100%)',
    padding: '0 0 24px',
    boxSizing: 'border-box',
    fontFamily: '"Segoe UI", system-ui, -apple-system, sans-serif',
  },
  shell: {
    maxWidth: 480,
    margin: '0 auto',
    padding: '0 16px',
  },
  topBar: {
    padding: '14px 0 8px',
  },
  backBtn: {
    border: 'none',
    background: 'rgba(255,255,255,0.2)',
    color: '#fff',
    fontWeight: 700,
    fontSize: 14,
    padding: '8px 14px',
    borderRadius: 999,
    cursor: 'pointer',
  },
  heroCard: {
    position: 'relative',
    background: '#fff',
    borderRadius: 24,
    padding: '24px 20px 20px',
    marginBottom: 16,
    boxShadow: '0 20px 50px rgba(15, 23, 42, 0.12)',
    overflow: 'hidden',
  },
  heroGlow: {
    position: 'absolute',
    top: -40,
    right: -40,
    width: 120,
    height: 120,
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #99f6e4, #ccfbf1)',
    opacity: 0.6,
  },
  badge: {
    display: 'inline-block',
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: '#0f766e',
    background: '#ecfdf5',
    padding: '6px 10px',
    borderRadius: 8,
    marginBottom: 12,
  },
  heroTitle: {
    margin: 0,
    fontSize: 28,
    lineHeight: 1.15,
    color: '#0f172a',
    fontWeight: 800,
    letterSpacing: '-0.02em',
  },
  company: {
    margin: '10px 0 6px',
    fontSize: 17,
    fontWeight: 700,
    color: '#334155',
  },
  locationLine: {
    margin: 0,
    fontSize: 14,
    color: '#64748b',
    lineHeight: 1.5,
    display: 'flex',
    alignItems: 'flex-start',
    gap: 6,
  },
  pin: { flexShrink: 0 },
  highlightRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 10,
    marginTop: 18,
  },
  highlight: {
    background: 'linear-gradient(145deg, #f0fdfa, #ecfeff)',
    border: '1px solid #99f6e4',
    borderRadius: 14,
    padding: '12px 14px',
  },
  highlightLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: '#0f766e',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  highlightValue: {
    marginTop: 4,
    fontSize: 14,
    fontWeight: 800,
    color: '#134e4a',
    lineHeight: 1.35,
  },
  section: {
    background: '#fff',
    borderRadius: 20,
    padding: '18px 18px 8px',
    marginBottom: 12,
    boxShadow: '0 4px 20px rgba(15, 23, 42, 0.06)',
  },
  sectionTitle: {
    margin: '0 0 14px',
    fontSize: 16,
    fontWeight: 800,
    color: '#0f172a',
  },
  detailsGrid: {
    display: 'grid',
    gap: 4,
  },
  detailRow: {
    display: 'flex',
    gap: 12,
    padding: '12px 0',
    borderBottom: '1px solid #f1f5f9',
  },
  detailIcon: {
    fontSize: 18,
    lineHeight: 1,
    marginTop: 2,
  },
  detailLabel: {
    fontSize: 11,
    color: '#94a3b8',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.03em',
  },
  detailValue: {
    fontSize: 15,
    color: '#1e293b',
    fontWeight: 600,
    lineHeight: 1.4,
    marginTop: 2,
    wordBreak: 'break-word',
  },
  description: {
    margin: '0 0 10px',
    fontSize: 15,
    lineHeight: 1.6,
    color: '#475569',
    whiteSpace: 'pre-wrap',
  },
  footerNote: {
    textAlign: 'center',
    fontSize: 12,
    color: '#94a3b8',
    margin: '8px 0 16px',
  },
  successBox: {
    background: '#fff',
    borderRadius: 20,
    padding: 22,
    marginBottom: 12,
    boxShadow: '0 8px 30px rgba(16, 185, 129, 0.15)',
    border: '1px solid #a7f3d0',
    textAlign: 'center',
  },
  successIcon: {
    width: 48,
    height: 48,
    borderRadius: '50%',
    background: '#10b981',
    color: '#fff',
    fontSize: 24,
    fontWeight: 800,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 12px',
  },
  contactCard: {
    background: '#f0fdfa',
    borderRadius: 14,
    padding: 16,
    textAlign: 'left',
  },
  phone: {
    display: 'inline-block',
    marginTop: 8,
    color: '#0f766e',
    fontWeight: 800,
    fontSize: 20,
    textDecoration: 'none',
  },
  applySheet: {
    background: '#fff',
    borderRadius: 20,
    padding: 20,
    marginBottom: 12,
    boxShadow: '0 8px 28px rgba(15, 23, 42, 0.08)',
    scrollMarginTop: 16,
    scrollMarginBottom: 100,
    animation: 'applySlideIn 0.35s ease-out',
  },
  applyHint: {
    margin: '-6px 0 12px',
    fontSize: 14,
    color: '#64748b',
  },
  label: {
    display: 'block',
    marginTop: 12,
    marginBottom: 6,
    fontSize: 13,
    fontWeight: 700,
    color: '#334155',
  },
  input: {
    width: '100%',
    boxSizing: 'border-box',
    padding: '14px 16px',
    borderRadius: 14,
    border: '1px solid #e2e8f0',
    fontSize: 16,
    background: '#f8fafc',
  },
  row: { display: 'flex', gap: 10 },
  workLocNote: {
    margin: '14px 0 0',
    fontSize: 13,
    color: '#64748b',
  },
  primaryBtn: {
    marginTop: 18,
    width: '100%',
    padding: '16px',
    border: 'none',
    borderRadius: 16,
    background: 'linear-gradient(135deg, #0d9488, #0f766e)',
    color: '#fff',
    fontWeight: 800,
    fontSize: 16,
    cursor: 'pointer',
    boxShadow: '0 8px 20px rgba(13, 148, 136, 0.35)',
  },
  primaryLinkBtn: {
    display: 'inline-block',
    padding: '14px 20px',
    borderRadius: 14,
    background: '#0d9488',
    color: '#fff',
    fontWeight: 800,
    textDecoration: 'none',
  },
  outlineBtn: {
    padding: '12px 18px',
    borderRadius: 14,
    border: '2px solid #0d9488',
    background: '#fff',
    color: '#0f766e',
    fontWeight: 700,
    cursor: 'pointer',
  },
  textBtn: {
    marginTop: 10,
    width: '100%',
    padding: '10px',
    border: 'none',
    background: 'transparent',
    color: '#64748b',
    fontWeight: 600,
    cursor: 'pointer',
  },
  stickyBar: {
    position: 'fixed',
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    gap: 10,
    padding: '12px 16px calc(12px + env(safe-area-inset-bottom))',
    background: 'rgba(255,255,255,0.96)',
    backdropFilter: 'blur(12px)',
    borderTop: '1px solid #e2e8f0',
    boxShadow: '0 -10px 40px rgba(15, 23, 42, 0.08)',
    zIndex: 20,
    maxWidth: 480,
    margin: '0 auto',
    boxSizing: 'border-box',
  },
  stickySecondary: {
    flex: '0 0 32%',
    padding: '15px 8px',
    borderRadius: 16,
    border: '1px solid #cbd5e1',
    background: '#fff',
    color: '#334155',
    fontWeight: 700,
    fontSize: 15,
    cursor: 'pointer',
  },
  stickyPrimary: {
    flex: 1,
    padding: '15px 8px',
    borderRadius: 16,
    border: 'none',
    background: 'linear-gradient(135deg, #0d9488, #0f766e)',
    color: '#fff',
    fontWeight: 800,
    fontSize: 15,
    cursor: 'pointer',
    boxShadow: '0 6px 16px rgba(13, 148, 136, 0.3)',
  },
  loadingCard: {
    background: '#fff',
    borderRadius: 20,
    padding: 32,
    textAlign: 'center',
    marginTop: 40,
    boxShadow: '0 10px 30px rgba(15, 23, 42, 0.08)',
  },
  spinner: {
    width: 36,
    height: 36,
    border: '3px solid #e2e8f0',
    borderTopColor: '#0d9488',
    borderRadius: '50%',
    margin: '0 auto',
    animation: 'spin 0.8s linear infinite',
  },
  emptyCard: {
    background: '#fff',
    borderRadius: 20,
    padding: 28,
    marginTop: 24,
    textAlign: 'center',
  },
  sub: {
    color: '#64748b',
    lineHeight: 1.5,
  },
};

export default JobInterestJobPage;
